import type { StrokePoint, StrokePoints } from "./stroke-shape.js";

/**
 * Where a character was drawn, and where each of its strokes turns.
 *
 * The two questions `characterShape` has to answer before it can describe
 * anything, and both are geometry over the points a pointer or a median hands
 * over. `stroke-shape.ts` is what they are for.
 */

/**
 * How far a point may sit off a straight line before the run is split there.
 *
 * A fraction of the character's span. At 0.06 the medians come to 1.31 runs
 * per stroke. Lowering it to 0.04 finds 1.46 and raising it to 0.11 finds 1.19,
 * and the middle of that range is where a 折 reliably splits and a wobbly 横
 * reliably does not.
 */
export const cornerTolerance = 0.06;

/** The box a set of strokes was drawn in. */
export interface StrokeBounds {
  readonly left: number;
  readonly top: number;
  readonly span: number;
}

/**
 * The box the strokes occupy, as a square.
 *
 * Square rather than the bounding rectangle, and that is what keeps 一 and 丨
 * apart. Scaling each axis to fill the box would stretch both into the same
 * line across the middle of it.
 */
export function strokeBounds(strokes: readonly StrokePoints[]): StrokeBounds {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;

  for (const stroke of strokes) {
    for (const [x, y] of stroke) {
      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
    }
  }

  const width = right - left;
  const height = bottom - top;
  const span = Math.max(width, height, Number.EPSILON);

  return {
    left: left - (span - width) / 2,
    top: top - (span - height) / 2,
    span,
  };
}

/** How far a point sits off the line between two others. */
function offLine(
  point: StrokePoint,
  from: StrokePoint,
  to: StrokePoint,
): number {
  const runX = to[0] - from[0];
  const runY = to[1] - from[1];
  const length = Math.hypot(runX, runY);

  if (length === 0) {
    return Math.hypot(point[0] - from[0], point[1] - from[1]);
  }

  return (
    Math.abs(runY * (point[0] - from[0]) - runX * (point[1] - from[1])) / length
  );
}

/**
 * The points of a stroke that a straight run has to start or end at.
 *
 * Ramer-Douglas-Peucker. The point furthest off the line between the two ends
 * is kept whenever it is further off than the tolerance, and each half is then
 * asked the same question. What comes back is the corners of the stroke and
 * nothing else, which is what a 折 has and a wobbly 横 does not.
 */
export function strokeCorners(
  points: StrokePoints,
  tolerance: number,
): readonly StrokePoint[] {
  if (points.length < 3) {
    return points;
  }

  const first = points[0] as StrokePoint;
  const last = points[points.length - 1] as StrokePoint;
  let furthest = 0;
  let at = -1;

  for (let index = 1; index < points.length - 1; index += 1) {
    const off = offLine(points[index] as StrokePoint, first, last);

    if (off > furthest) {
      furthest = off;
      at = index;
    }
  }

  if (furthest <= tolerance || at < 0) {
    return [first, last];
  }

  return [
    ...strokeCorners(points.slice(0, at + 1), tolerance).slice(0, -1),
    ...strokeCorners(points.slice(at), tolerance),
  ];
}
